import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  fetchBooths,
  fetchPublicSpeakers,
  toggleBookmarkedSession,
  Booth,
  Speaker,
} from '../lib/api';
import {
  eventInfo,
  guestsOfHonor,
  industrySpeakers,
  pastEditionSponsors,
  industryPartners,
  focusSectors,
  defaultConfirmedSpeakers,
  agendaSessions,
  buildDynamicAgendaSessions,
  AgendaSession,
} from '../data';
import {
  ArrowRight,
  Building,
  Building2,
  Calendar,
  MapPin,
  Ticket,
  ShieldCheck,
  Globe,
  Users,
  TrendingUp,
  Download,
  FlaskConical,
  Microscope,
  Factory,
  Landmark,
  Cpu,
  Layers,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Mic,
  Clock,
  ExternalLink,
  FileText,
  Linkedin,
  Plane,
  Navigation,
  ScanLine,
  Maximize2,
  Sparkles,
  Award,
  Crown,
  Medal,
  Shield,
  Coffee,
  ShoppingBag,
  Utensils,
  Mail,
  Newspaper,
  Bookmark,
  BookmarkCheck,
  Filter,
} from 'lucide-react';
import { VenueMapSection } from '../components/VenueMapSection';

function SpeakerAvatar({ src, alt, name }: { src?: string | null; alt: string; name: string }) {
  const [error, setError] = useState(false);
  const initials = name
    .split(' ')
    .filter((n) => !n.startsWith('Dr') && !n.startsWith('Shri'))
    .map((n) => n[0])
    .slice(0, 2)
    .join('');

  if (error || !src) {
    return (
      <div className="w-full h-full bg-gradient-to-tr from-[#0A3D27] to-[#1E6B48] flex items-center justify-center text-white font-black text-xl">
        {initials || 'SP'}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      onError={() => setError(true)}
      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
      loading="lazy"
    />
  );
}

function SectorIcon({ name, className }: { name: string; className?: string }) {
  const iconClass = className || 'w-5 h-5';
  switch (name) {
    case 'ShieldCheck':
      return <ShieldCheck className={`${iconClass} text-[#0A3D27]`} />;
    case 'FlaskConical':
      return <FlaskConical className={`${iconClass} text-teal-700`} />;
    case 'Microscope':
      return <Microscope className={`${iconClass} text-cyan-700`} />;
    case 'Factory':
      return <Factory className={`${iconClass} text-[#785613]`} />;
    case 'Landmark':
      return <Landmark className={`${iconClass} text-indigo-700`} />;
    case 'Cpu':
    default:
      return <Cpu className={`${iconClass} text-purple-700`} />;
  }
}

const heroVideos = [
  {
    id: 'agtech',
    title: 'AgTech & Digitization',
    subtitle: 'Precision Farming & Autonomous Robotics',
    src: '/videos/AgTech_Digitization.mp4',
  },
  {
    id: 'agrochemicals',
    title: 'Crop Protection & Formulations',
    subtitle: 'Technical Grade Actives & Biological Solutions',
    src: '/videos/Agrochemicals_CropProtection.mp4',
  },
  {
    id: 'specialty-chemicals',
    title: 'Allied & Specialty Chemicals',
    subtitle: 'Surfactants, Solvents & Intermediates',
    src: '/videos/Allied_SpecialtyChemicals.mp4',
  },
  {
    id: 'processing',
    title: 'Processing, Packaging & Supply Chain',
    subtitle: 'Cold Storage, Post-Harvest & Reefer Logistics',
    src: '/videos/Processing_Packaging.mp4',
  },
  {
    id: 'research',
    title: 'Research, Testing & Regulatory',
    subtitle: 'GLP Residue Studies & Bio-Efficacy Validation',
    src: '/videos/Research_Testing_Regulatory.mp4',
  },
  {
    id: 'finance',
    title: 'Agri Finance & Advisory',
    subtitle: 'Credit, Warehousing & Export Trade Advisory',
    src: '/videos/Finance_Advisory.mp4',
  },
];

function CompactSessionCard({
  session,
  isBookmarked = false,
  onToggleBookmark,
}: {
  session: AgendaSession;
  isBookmarked?: boolean;
  onToggleBookmark?: (session: AgendaSession) => void;
}) {
  const isKeynote = session.format === 'keynote';

  return (
    <div className="bg-white border border-slate-200/90 hover:border-[#0A3D27]/40 hover:shadow-md transition-all rounded-2xl p-3.5 space-y-2 group">
      {/* Top Meta Bar */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#0A3D27] bg-emerald-50 border border-emerald-200/60 px-2 py-0.5 rounded-md">
            <Clock className="w-3 h-3 text-[#0A3D27]" />
            <span>{session.timeSlot}</span>
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 font-medium truncate max-w-[140px]">
            <MapPin className="w-3 h-3 text-[#0A3D27] shrink-0" />
            <span className="truncate">{session.hall}</span>
          </span>
          {session.track && (
            <span className="text-[9px] font-bold text-[#785613] bg-[#FAF5EA] border border-[#E6C687]/80 px-2 py-0.5 rounded-md truncate max-w-[145px]">
              {session.track}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {/* Format Pill */}
          <span
            className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0 ${
              isKeynote
                ? 'text-[#0A3D27] bg-[#0A3D27]/10 border border-[#0A3D27]/20'
                : 'text-[#785613] bg-[#FAF5EA] border border-[#E6C687]'
            }`}
          >
            {isKeynote ? 'Keynote Talk' : 'Panel Discussion'}
          </span>

          {onToggleBookmark && (
            <button
              type="button"
              onClick={() => onToggleBookmark(session)}
              title={isBookmarked ? 'Saved in My Schedule' : 'Bookmark Session to My Schedule'}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-extrabold border transition-all cursor-pointer ${
                isBookmarked
                  ? 'bg-[#0A3D27] text-[#FAF5EA] border-[#0A3D27]'
                  : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-[#0A3D27] hover:text-[#0A3D27]'
              }`}
            >
              <Bookmark className={`w-2.5 h-2.5 ${isBookmarked ? 'fill-current' : ''}`} />
              <span>{isBookmarked ? 'Saved' : 'Save'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Session Title */}
      <div>
        <h5 className="text-xs sm:text-sm font-black text-slate-900 leading-snug group-hover:text-[#0A3D27] transition-colors line-clamp-2">
          {session.title}
        </h5>
        {session.abstract && (
          <p className="text-[11px] text-slate-500 line-clamp-1 mt-0.5 leading-tight">
            {session.abstract}
          </p>
        )}
      </div>

      {/* Speaker or Panel Info (Ultra-Compact) */}
      <div className="pt-2 border-t border-slate-100">
        {isKeynote && session.speaker ? (
          /* Individual Keynote Speaker */
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 shrink-0">
                <SpeakerAvatar
                  src={session.speaker.imageSrc}
                  alt={session.speaker.name}
                  name={session.speaker.name}
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold text-slate-900 truncate leading-tight">
                  {session.speaker.name}
                </div>
                <div className="text-[10px] text-slate-500 truncate leading-tight">
                  {session.speaker.designation} • {session.speaker.organization}
                </div>
              </div>
            </div>
            {session.speaker.linkedinUrl && (
              <a
                href={session.speaker.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-[#0A3D27] p-1 shrink-0 transition-colors"
                title="LinkedIn Profile"
              >
                <Linkedin className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        ) : (
          /* Panel Discussion: Moderator + Contributing Panelists */
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 flex-wrap">
              {session.moderator && (
                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-slate-50 border border-slate-200 rounded-lg text-[10px]">
                  <div className="w-4 h-4 rounded-full overflow-hidden bg-slate-200 shrink-0">
                    <SpeakerAvatar
                      src={session.moderator.imageSrc}
                      alt={session.moderator.name}
                      name={session.moderator.name}
                    />
                  </div>
                  <span className="text-slate-400 font-bold uppercase text-[8px]">Mod:</span>
                  <span className="font-bold text-slate-800 truncate max-w-[100px]">
                    {session.moderator.name}
                  </span>
                </div>
              )}

              {session.panelists?.map((p) => (
                <div
                  key={p.id}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-white border border-slate-200 rounded-lg text-[10px] hover:border-slate-300"
                  title={`${p.name} (${p.role}, ${p.organization})`}
                >
                  <div className="w-4 h-4 rounded-full overflow-hidden bg-slate-100 shrink-0">
                    <SpeakerAvatar src={p.imageSrc} alt={p.name} name={p.name} />
                  </div>
                  <span className="font-semibold text-slate-700 truncate max-w-[90px] sm:max-w-[110px]">
                    {p.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function HomePage() {
  const [activeHeroVideoIndex, setActiveHeroVideoIndex] = useState<number>(0);
  const heroVideoRef = useRef<HTMLVideoElement>(null);

  const handleNextHeroVideo = () => {
    setActiveHeroVideoIndex((prev) => (prev + 1) % heroVideos.length);
  };

  useEffect(() => {
    if (heroVideoRef.current) {
      heroVideoRef.current.play().catch(() => {});
    }
  }, [activeHeroVideoIndex]);

  const [activeSectorId, setActiveSectorId] = useState<string>(
    focusSectors[0]?.id || 'agrochemicals-crop-protection'
  );
  const [isSectorHovered, setIsSectorHovered] = useState<boolean>(false);
  const [sectorProgress, setSectorProgress] = useState<number>(0);
  const progressRef = useRef<number>(0);

  const activeSector = focusSectors.find((s) => s.id === activeSectorId) || focusSectors[0];
  const activeSectorIndex = focusSectors.findIndex((s) => s.id === activeSector.id);

  const handlePrevSector = () => {
    progressRef.current = 0;
    setSectorProgress(0);
    const nextIdx = (activeSectorIndex - 1 + focusSectors.length) % focusSectors.length;
    setActiveSectorId(focusSectors[nextIdx].id);
  };

  const handleNextSector = () => {
    progressRef.current = 0;
    setSectorProgress(0);
    const nextIdx = (activeSectorIndex + 1) % focusSectors.length;
    setActiveSectorId(focusSectors[nextIdx].id);
  };

  const handleSelectSector = (sectorId: string) => {
    progressRef.current = 0;
    setSectorProgress(0);
    setActiveSectorId(sectorId);
  };

  // Time-based smooth auto-rotation through sectors with countdown progress wheel
  const ROTATION_DURATION_MS = 5000;
  const TICK_INTERVAL_MS = 50;

  useEffect(() => {
    if (isSectorHovered) return;

    const timer = setInterval(() => {
      progressRef.current += (TICK_INTERVAL_MS / ROTATION_DURATION_MS) * 100;

      if (progressRef.current >= 100) {
        progressRef.current = 0;
        setSectorProgress(0);
        setActiveSectorId((currentId) => {
          const currIdx = focusSectors.findIndex((s) => s.id === currentId);
          const nextIdx = (currIdx + 1) % focusSectors.length;
          return focusSectors[nextIdx].id;
        });
      } else {
        setSectorProgress(progressRef.current);
      }
    }, TICK_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [isSectorHovered]);

  const { data: booths = [] } = useQuery<Booth[]>({
    queryKey: ['booths'],
    queryFn: () => fetchBooths(),
    refetchInterval: 15000,
  });

  const { data: dbSpeakers = [] } = useQuery<Speaker[]>({
    queryKey: ['public-speakers'],
    queryFn: () => fetchPublicSpeakers(),
    staleTime: 60000,
  });

  const speakersToDisplay = dbSpeakers.length > 0 ? dbSpeakers : defaultConfirmedSpeakers;

  const [agendaViewMode, setAgendaViewMode] = useState<'agenda' | 'directory'>('agenda');
  const [selectedAgendaTrack, setSelectedAgendaTrack] = useState<string>('all');
  const [bookmarkedIds, setBookmarkedIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('iiab_bookmarked_sessions');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const handleToggleSessionBookmark = async (session: AgendaSession) => {
    const exists = bookmarkedIds.includes(session.id);
    const nextIds = exists
      ? bookmarkedIds.filter((id) => id !== session.id)
      : [...bookmarkedIds, session.id];
    setBookmarkedIds(nextIds);
    try {
      localStorage.setItem('iiab_bookmarked_sessions', JSON.stringify(nextIds));
    } catch {
      // Ignore storage errors
    }

    const portalEmail = sessionStorage.getItem('iiab_portal_email') || undefined;
    const portalTok = sessionStorage.getItem('iiab_portal_token') || undefined;
    if (portalEmail || portalTok) {
      toggleBookmarkedSession(
        {
          sessionId: session.id,
          action: exists ? 'remove' : 'add',
          session: {
            id: session.id,
            title: session.title,
            date: session.date,
            startTime: session.startTime,
            endTime: session.endTime,
            hall: session.hall,
            track: session.track,
            speakerName: session.speaker?.name || session.moderator?.name || 'IIAB 2027',
          },
        },
        portalEmail,
        portalTok
      ).catch(() => {});
    }
  };

  const availableCount = booths.length > 0 ? booths.filter((b) => b.status === 'available').length : 108;
  const onHoldCount = booths.length > 0 ? booths.filter((b) => b.status === 'on_hold').length : 7;
  const bookedCount = booths.length > 0 ? booths.filter((b) => b.status === 'booked').length : 5;

  const mergedAgendaSessions = buildDynamicAgendaSessions(dbSpeakers);
  const agendaTracks = Array.from(
    new Set(mergedAgendaSessions.map((s) => s.track).filter(Boolean))
  );
  const filteredAgendaSessions = mergedAgendaSessions.filter(
    (s) => selectedAgendaTrack === 'all' || s.track === selectedAgendaTrack
  );
  const day1Sessions = filteredAgendaSessions.filter((session) => session.day === 1);
  const day2Sessions = filteredAgendaSessions.filter((session) => session.day === 2);

  const allDirectorySpeakers = [
    ...speakersToDisplay.map((s) => ({
      id: s.id,
      name: s.fullName,
      role: s.designation,
      organization: s.organization,
      imageSrc: s.headshotUrl,
      track: s.track,
      isKeynote: true,
      stageOrHall: s.stageOrHall,
      time: s.sessionStartTime ? `${s.sessionDate?.slice(5)} • ${s.sessionStartTime}` : undefined,
      linkedinUrl: s.linkedinUrl,
    })),
    ...industrySpeakers.map((p) => ({
      id: p.id,
      name: p.name,
      role: p.role,
      organization: p.organization,
      imageSrc: p.imageSrc,
      track: 'Agrochemicals & Crop Protection',
      isKeynote: false,
      stageOrHall: 'Conference Hall Stage',
      time: 'Day 1 & Day 2',
      linkedinUrl: undefined,
    })),
  ];

  return (
    <div className="space-y-16">
      {/* 1. HERO SECTION (Executive Light Luxury Card with Ambient Video Background) */}
      <section
        id="hero"
        className="scroll-mt-24 relative overflow-hidden rounded-3xl bg-white border border-slate-200/90 p-8 sm:p-12 lg:p-16 shadow-lg shadow-emerald-950/5"
      >
        {/* Subtle Ambient Video Background Layer */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
          <video
            ref={heroVideoRef}
            key={heroVideos[activeHeroVideoIndex].src}
            src={heroVideos[activeHeroVideoIndex].src}
            autoPlay
            loop={false}
            muted
            playsInline
            onEnded={handleNextHeroVideo}
            className="w-full h-full object-cover opacity-65 transition-opacity duration-1000 scale-[1.02]"
          />
          {/* Frosted Gradient Masks: Text shield on left, open on right for cinematic clarity */}
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-white/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-transparent to-white/40" />
        </div>

        {/* Ambient Decorative Blurs */}
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 rounded-full bg-emerald-500/5 blur-3xl pointer-events-none z-[1]" />
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-96 h-96 rounded-full bg-[#C5A059]/10 blur-3xl pointer-events-none z-[1]" />

        <div className="relative z-10 max-w-3xl space-y-6">
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#0F172A] leading-tight">
            India International Agri Business{' '}
            <span className="bg-gradient-to-r from-[#0A3D27] via-[#155e3b] to-[#785613] bg-clip-text text-transparent">
              Conference & Expo
            </span>
          </h1>

          <p className="text-base sm:text-lg text-slate-600 leading-relaxed font-normal">
            {eventInfo.tagline}. Join 150+ leading agrochemical manufacturers, specialty chemical synthesizers,
            and global trade delegations at India's premier B2B inputs summit.
          </p>

          {/* Event Schedule & Location Pills */}
          <div className="flex flex-wrap gap-4 pt-2 text-xs">
            <div className="flex items-center gap-2 bg-[#F8F9F6]/90 backdrop-blur-xs px-4 py-2.5 rounded-xl border border-slate-200 shadow-2xs">
              <Calendar className="w-4 h-4 text-[#0A3D27] shrink-0" />
              <div>
                <span className="font-bold text-slate-900 block">{eventInfo.dates.display}</span>
                <span className="text-[11px] text-slate-500">{eventInfo.dates.hours}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 bg-[#F8F9F6]/90 backdrop-blur-xs px-4 py-2.5 rounded-xl border border-slate-200 shadow-2xs">
              <MapPin className="w-4 h-4 text-[#0A3D27] shrink-0" />
              <div>
                <span className="font-bold text-slate-900 block">{eventInfo.venue.shortName}</span>
                <span className="text-[11px] text-slate-500">BKC, Mumbai, Maharashtra</span>
              </div>
            </div>
          </div>

          {/* Direct Action Buttons */}
          <div className="flex flex-wrap items-center gap-3 pt-4">
            <Link
              to="/floorplan"
              className="px-6 py-3.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white font-extrabold text-sm rounded-xl transition-all shadow-md shadow-emerald-950/20 hover:shadow-lg flex items-center gap-2 cursor-pointer"
            >
              <Building2 className="w-4 h-4" />
              <span>Reserve Exhibition Stall</span>
              <ArrowRight className="w-4 h-4" />
            </Link>

            <Link
              to="/register"
              className="px-6 py-3.5 bg-[#FAF5EA] hover:bg-[#F5E8C7] text-[#785613] font-bold text-sm border border-[#E6C687] rounded-xl transition-all flex items-center gap-2 cursor-pointer shadow-2xs"
            >
              <Ticket className="w-4 h-4 text-[#785613]" />
              <span>Register as Delegate</span>
            </Link>
          </div>
        </div>

        {/* Metrics Strip */}
        <div className="relative z-10 mt-6 pt-6 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-6 bg-white/50 backdrop-blur-xs rounded-2xl p-4 sm:p-5 border border-slate-100/80">
          <div>
            <div className="text-2xl sm:text-3xl font-black text-slate-900">150+</div>
            <div className="text-xs text-slate-500 mt-0.5">Exhibiting Brands</div>
          </div>
          <div>
            <div className="text-2xl sm:text-3xl font-black text-slate-900">5,000+</div>
            <div className="text-xs text-slate-500 mt-0.5">B2B Trade Delegates</div>
          </div>
          <div>
            <div className="text-2xl sm:text-3xl font-black text-slate-900">40+</div>
            <div className="text-xs text-slate-500 mt-0.5">Export Target Nations</div>
          </div>
          <div>
            <div className="text-2xl sm:text-3xl font-black text-[#0A3D27]">$16 Billion</div>
            <div className="text-xs text-slate-500 mt-0.5">Ag-Inputs Opportunity 2030</div>
          </div>
        </div>
      </section>

      {/* 2. MARKET CONTEXT SECTION */}
      <section id="impact" className="space-y-6 scroll-mt-24 relative">
        <span id="about" className="scroll-mt-24 absolute -top-24 pointer-events-none" />
        <div className="max-w-2xl">
          <span className="text-xs font-bold text-[#0A3D27] uppercase tracking-wider block">
            Strategic Market Growth
          </span>
          <h2 className="text-2xl sm:text-3xl font-black text-[#0F172A] tracking-tight mt-1">
            A USD 16 Billion Opportunity by 2030
          </h2>
          <p className="text-sm text-slate-600 mt-2">
            India represents one of the world's most dynamic agricultural input markets. With 140 million
            hectares of arable land and 15+ diverse agro-climatic zones, the nation is accelerating its shift
            toward high-value commercial crops and scientific formulation chemistry.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="bg-white border border-slate-200/90 rounded-2xl p-6 space-y-3 shadow-xs hover:shadow-md transition-shadow">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-[#0A3D27] border border-emerald-200/60 flex items-center justify-center font-black">
              <Globe className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold text-slate-900">140M Hectares Arable Land</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              India ranks among the top global producers of wheat, rice, pulses, sugarcane, and cotton, demanding
              intensive, science-led plant protection chemistry and microbial solutions.
            </p>
          </div>

          <div className="bg-white border border-slate-200/90 rounded-2xl p-6 space-y-3 shadow-xs hover:shadow-md transition-shadow">
            <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-800 border border-teal-200/60 flex items-center justify-center font-black">
              <TrendingUp className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold text-slate-900">15+ Agro-Climatic Zones</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Diverse crop systems support commercial horticulture, plantations, and contract farming, creating
              immense regional demand for specialized crop nutrients and bio-stimulants.
            </p>
          </div>

          <div className="bg-white border border-slate-200/90 rounded-2xl p-6 space-y-3 shadow-xs hover:shadow-md transition-shadow">
            <div className="w-10 h-10 rounded-xl bg-[#FAF5EA] text-[#785613] border border-[#E6C687] flex items-center justify-center font-black">
              <Users className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold text-slate-900">Toll Manufacturing & M&A</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              IIAB connects global synthesis innovators with Indian contract manufacturers, formulation plants,
              and distributor networks for seamless market entry.
            </p>
          </div>
        </div>
      </section>

      {/* 3. FOCUS SECTORS (COMPACT SINGLE-VIEW INTERACTIVE SHOWCASE) */}
      <section
        id="sectors"
        className="space-y-4 scroll-mt-24"
        onMouseEnter={() => setIsSectorHovered(true)}
        onMouseLeave={() => setIsSectorHovered(false)}
        onFocus={() => setIsSectorHovered(true)}
        onBlur={() => setIsSectorHovered(false)}
      >
        {/* Clean Header with Title */}
        <div className="pb-3 border-b border-slate-200">
          <span className="text-xs font-bold text-[#0A3D27] uppercase tracking-wider block">
            Exhibition Profiles
          </span>
          <h2 className="text-xl sm:text-2xl font-black text-[#0F172A] tracking-tight mt-0.5">
            6 Core Focus Industry Sectors
          </h2>
          <p className="text-xs text-slate-600 mt-0.5">
            Explore specialized exhibiting segments covering the complete agri-input supply chain.
          </p>
        </div>

        {/* INTERACTIVE MASTER-DETAIL SHOWCASE (FITS 1 SCREEN) */}
        <div className="space-y-3">
          {/* Mobile / Tablet Horizontal Segmented Bar (< lg) */}
          <div className="lg:hidden flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
            {focusSectors.map((sector) => {
              const isActive = sector.id === activeSector.id;
              return (
                <button
                  key={sector.id}
                  type="button"
                  onClick={() => handleSelectSector(sector.id)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap shrink-0 flex items-center gap-1.5 border transition-all cursor-pointer ${
                    isActive
                      ? 'bg-[#0A3D27] text-white border-[#0A3D27] shadow-xs'
                      : 'bg-white text-slate-700 hover:bg-slate-50 border-slate-200'
                  }`}
                >
                  <span className="opacity-75 font-mono">0{sector.number}</span>
                  <span>{sector.title.split('&')[0].trim()}</span>
                </button>
              );
            })}
          </div>

          {/* Desktop Side-by-Side Master-Detail Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
            {/* Left Selector Rail (All 6 Sectors at a glance) */}
            <div className="hidden lg:flex lg:col-span-5 flex-col gap-1.5">
              {focusSectors.map((sector) => {
                const isActive = sector.id === activeSector.id;
                return (
                  <button
                    key={sector.id}
                    type="button"
                    onClick={() => handleSelectSector(sector.id)}
                    onMouseEnter={() => {
                      setIsSectorHovered(true);
                      handleSelectSector(sector.id);
                    }}
                    className={`w-full text-left px-3 py-2.5 rounded-2xl border transition-all flex items-center justify-between group cursor-pointer ${
                      isActive
                        ? 'bg-white border-[#0A3D27] ring-2 ring-[#0A3D27]/15 shadow-sm'
                        : 'bg-slate-50/70 hover:bg-white hover:border-slate-300 border-slate-200/80'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div
                        className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 border transition-transform ${
                          isActive
                            ? 'bg-emerald-50 border-emerald-200 text-[#0A3D27] scale-105 shadow-2xs'
                            : 'bg-white border-slate-200 text-slate-500 group-hover:scale-105'
                        }`}
                      >
                        <SectorIcon name={sector.iconName} className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 pr-1">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`text-[10px] font-black tracking-tight font-mono ${
                              isActive ? 'text-[#0A3D27]' : 'text-slate-400'
                            }`}
                          >
                            0{sector.number}
                          </span>
                          <h4
                            className={`text-xs font-bold truncate transition-colors ${
                              isActive ? 'text-[#0A3D27]' : 'text-slate-800 group-hover:text-slate-900'
                            }`}
                          >
                            {sector.title}
                          </h4>
                        </div>
                        <p className="text-[11px] text-slate-500 truncate mt-0.5">{sector.subtitle}</p>
                      </div>
                    </div>
                    <ChevronRight
                      className={`w-4 h-4 shrink-0 transition-transform ${
                        isActive
                          ? 'text-[#0A3D27] translate-x-0.5'
                          : 'text-slate-400 group-hover:text-slate-600'
                      }`}
                    />
                  </button>
                );
              })}
            </div>

            {/* Right Detail Showcase Panel */}
            <div className="lg:col-span-7 bg-white border border-slate-200/90 rounded-3xl p-5 shadow-sm flex flex-col justify-between relative overflow-hidden">
              <div className="absolute top-0 right-0 -mt-12 -mr-12 w-44 h-44 bg-emerald-500/5 rounded-full blur-2xl pointer-events-none" />

              <div className="space-y-3.5">
                {/* Top Row: Icon + Badges + Prev/Next Controls */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-emerald-50 border border-emerald-200/80 flex items-center justify-center shrink-0 shadow-2xs">
                      <SectorIcon name={activeSector.iconName} className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#0A3D27] bg-emerald-50 border border-emerald-200/60 px-2 py-0.5 rounded-md font-mono">
                          Sector 0{activeSector.number}
                        </span>
                        <span className="text-[11px] text-slate-400 font-medium">Exhibition Segment</span>
                      </div>
                      <h3 className="text-base sm:text-lg font-black text-slate-900 tracking-tight mt-0.5">
                        {activeSector.title}
                      </h3>
                      <p className="text-xs font-semibold text-[#0A3D27] mt-0.5">{activeSector.subtitle}</p>
                    </div>
                  </div>

                  {/* Single Consolidated Control Chip (Countdown Wheel + Counter + Steppers + Status) */}
                  <div
                    className="flex items-center gap-1.5 bg-slate-50/90 hover:bg-slate-100/90 border border-slate-200/90 px-2 py-1 rounded-2xl shadow-2xs transition-colors shrink-0"
                    title={isSectorHovered ? 'Auto-rotation paused' : 'Auto-advancing (Hover to pause)'}
                  >
                    {/* Previous button */}
                    <button
                      type="button"
                      onClick={handlePrevSector}
                      className="p-1 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-white transition-all cursor-pointer"
                      title="Previous sector"
                      aria-label="Previous sector"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </button>

                    {/* Circular Loading/Countdown Wheel */}
                    <div className="relative flex items-center justify-center w-5 h-5 shrink-0">
                      <svg className="w-5 h-5 -rotate-90 transform" viewBox="0 0 20 20">
                        {/* Background track */}
                        <circle
                          cx="10"
                          cy="10"
                          r="8"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          className="text-slate-200"
                          fill="transparent"
                        />
                        {/* Animated progress circle */}
                        <circle
                          cx="10"
                          cy="10"
                          r="8"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeDasharray={2 * Math.PI * 8}
                          strokeDashoffset={2 * Math.PI * 8 * (1 - sectorProgress / 100)}
                          strokeLinecap="round"
                          className={`transition-[stroke-dashoffset] duration-75 ${
                            isSectorHovered ? 'text-amber-500' : 'text-[#0A3D27]'
                          }`}
                          fill="transparent"
                        />
                      </svg>
                      {/* Center indicator: mini pause bars when hovered, pulsing dot when running */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        {isSectorHovered ? (
                          <div className="flex gap-0.5 items-center justify-center">
                            <span className="w-0.5 h-1.5 bg-amber-600 rounded-full" />
                            <span className="w-0.5 h-1.5 bg-amber-600 rounded-full" />
                          </div>
                        ) : (
                          <span className="w-1.5 h-1.5 rounded-full bg-[#0A3D27]" />
                        )}
                      </div>
                    </div>

                    {/* Counter & Status Label */}
                    <div className="flex items-center gap-1.5 px-0.5">
                      <span className="font-mono text-slate-800 font-extrabold text-[11px] tracking-tight">
                        0{activeSector.number}
                        <span className="text-slate-400 font-normal">/</span>
                        0{focusSectors.length}
                      </span>
                      <span className="text-slate-300">•</span>
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wider ${
                          isSectorHovered ? 'text-amber-700' : 'text-[#0A3D27]'
                        }`}
                      >
                        {isSectorHovered ? 'Paused' : 'Auto'}
                      </span>
                    </div>

                    {/* Next button */}
                    <button
                      type="button"
                      onClick={handleNextSector}
                      className="p-1 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-white transition-all cursor-pointer"
                      title="Next sector"
                      aria-label="Next sector"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Sector Description */}
                <p className="text-xs text-slate-600 leading-relaxed bg-slate-50/70 border border-slate-100 p-2.5 rounded-xl">
                  {activeSector.description}
                </p>

                {/* Two-Column Structured Feature Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Key Sub-Categories */}
                  <div className="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-3 space-y-1.5">
                    <span className="text-[10px] font-black text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#0A3D27]" />
                      <span>Key Sub-Categories</span>
                    </span>
                    <div className="space-y-1">
                      {activeSector.subCategories.slice(0, 4).map((sub, i) => (
                        <div key={i} className="text-[11px] text-slate-700 flex items-start gap-1.5 leading-snug">
                          <span className="text-[#0A3D27] font-bold shrink-0">•</span>
                          <span className="line-clamp-1">{sub}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Target Attendees */}
                  <div className="bg-emerald-50/30 border border-emerald-200/50 rounded-2xl p-3 space-y-1.5 flex flex-col justify-between">
                    <div className="space-y-1.5">
                      <span className="text-[10px] font-black text-[#0A3D27] uppercase tracking-wider flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5 text-[#0A3D27]" />
                        <span>Target Decision Makers</span>
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {activeSector.keyAttendees.map((att, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 bg-white border border-emerald-200/80 text-slate-800 rounded-md text-[10px] font-medium shadow-2xs"
                          >
                            {att}
                          </span>
                        ))}
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-500 pt-1 border-t border-emerald-100/60 flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-[#0A3D27] shrink-0" />
                      <span className="truncate">Pavilion 3 Dedicated Exhibitor Zone</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Bottom Action Footer */}
              <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2.5 text-xs">
                <span className="text-[11px] text-slate-400 hidden sm:inline">
                  Hover anywhere to pause auto-rotation
                </span>

                <div className="flex items-center gap-2 ml-auto">
                  <Link
                    to="/floorplan"
                    className="px-4 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white font-extrabold rounded-xl transition-all shadow-md shadow-emerald-950/20 hover:shadow-lg flex items-center gap-2 text-xs cursor-pointer whitespace-nowrap"
                  >
                    <Building2 className="w-3.5 h-3.5" />
                    <span>Book a Stall</span>
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 py-2 bg-emerald-50 hover:bg-emerald-100 text-[#0A3D27] font-bold border border-emerald-200 rounded-xl transition-colors flex items-center gap-2 text-xs cursor-pointer whitespace-nowrap"
                  >
                    <Ticket className="w-3.5 h-3.5 text-[#0A3D27]" />
                    <span>Register to Attend</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. CONSOLIDATED KEYNOTES & AGENDA SECTION */}
      <section id="keynotes" className="space-y-8 scroll-mt-24 relative">
        <span id="speakers" className="scroll-mt-24 absolute -top-24 pointer-events-none" />
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-2 border-b border-slate-200">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0A3D27]/10 border border-[#0A3D27]/20 text-[#0A3D27] text-xs font-bold">
              <Users className="w-3.5 h-3.5" />
              <span>Leadership & Keynote Directory</span>
            </div>
            <h2 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
              Dignitaries, Keynotes & Speakers
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 max-w-2xl leading-relaxed">
              Hear from agricultural policymakers, global synthesizers, and technical researchers driving the future of
              crop protection, sustainable plant nutrition, and bio-stimulant innovation.
            </p>
          </div>

          {/* Apply to Speak CTA */}
          <div className="shrink-0 flex items-center gap-3">
            <Link
              to="/speakers/apply"
              className="px-5 py-3 bg-[#0A3D27] hover:bg-[#062B1B] text-white font-extrabold text-xs rounded-xl shadow-md shadow-[#0A3D27]/20 transition-all flex items-center gap-2 cursor-pointer"
            >
              <Mic className="w-4 h-4 text-[#C5A059]" />
              <span>Apply to Speak at IIAB 2027</span>
            </Link>
          </div>
        </div>

        {/* Presidential VIP Honor Roll: Distinguished Dignitaries & Patrons */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#FAF5EA] via-[#FDFBF7] to-[#F5EFEB] border border-[#E6C687] p-6 sm:p-8 shadow-md">
          <div className="absolute inset-0 opacity-[0.25] pointer-events-none bg-[radial-gradient(#E6C687_1px,transparent_1px)] [background-size:20px_20px]" />
          <div className="absolute -top-12 -right-12 w-64 h-64 bg-[#E6C687]/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-[#0A3D27]/5 rounded-full blur-2xl pointer-events-none" />

          <div className="relative z-10 space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#E6C687]/60 pb-4">
              <div>
                <span className="text-[11px] font-extrabold text-[#785613] uppercase tracking-wider block">
                  Government & International Trade
                </span>
                <h3 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight mt-0.5">
                  Distinguished Guests of Honor & Patrons
                </h3>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#E6C687] text-[#785613] text-xs font-semibold shadow-2xs self-start sm:self-auto">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0A3D27] animate-pulse" />
                <span>Inaugural Plenary Addresses • Pavilion 3</span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-1">
              {guestsOfHonor.map((guest) => (
                <div
                  key={guest.id}
                  className="bg-white border border-[#E6C687]/70 hover:border-[#0A3D27] rounded-2xl p-4 transition-all hover:shadow-md hover:-translate-y-0.5 flex flex-col justify-between group"
                >
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-full overflow-hidden shrink-0 ring-2 ring-[#E6C687] ring-offset-2 ring-offset-white shadow-xs">
                        <SpeakerAvatar src={guest.imageSrc} alt={guest.name} name={guest.name} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-[9px] font-extrabold uppercase tracking-wider text-[#785613] bg-[#FAF5EA] border border-[#E6C687] px-2 py-0.5 rounded-full inline-block">
                          Guest of Honor
                        </span>
                        <h4 className="text-sm font-bold text-slate-900 group-hover:text-[#0A3D27] transition-colors truncate mt-1" title={guest.name}>
                          {guest.name}
                        </h4>
                      </div>
                    </div>
                    <p className="text-xs text-slate-700 font-semibold leading-snug line-clamp-2">
                      {guest.role}
                    </p>
                    <p className="text-[11px] text-slate-500 line-clamp-1">
                      {guest.organization}
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] font-semibold">
                    <span className="text-[#0A3D27]">Inaugural Stage</span>
                    <span className="text-[10px] text-slate-500 font-mono">Day 1 • 10:00 AM</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Unified Summit Agenda & Speaker Matrix */}
        <div className="space-y-6">
          {/* Header & Controls Bar */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 pb-2">
            <div>
              <span className="text-xs font-bold text-[#0A3D27] uppercase tracking-wider block">
                Technical Summit & Keynote Program
              </span>
              <h3 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight mt-1">
                Conference Schedule & Speaker Matrix
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 mt-1 max-w-2xl">
                128 PAX Executive Hall keynotes seamlessly integrated with industry leadership panels across Day 1 & Day 2.
              </p>
            </div>

            {/* View Mode Toggle: Agenda Timeline vs All Speakers Directory */}
            <div className="flex items-center gap-1.5 p-1 bg-slate-100 border border-slate-200 rounded-xl self-start lg:self-auto text-xs font-bold">
              <button
                type="button"
                onClick={() => setAgendaViewMode('agenda')}
                className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                  agendaViewMode === 'agenda'
                    ? 'bg-[#0A3D27] text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Chronological Agenda
              </button>
              <button
                type="button"
                onClick={() => setAgendaViewMode('directory')}
                className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                  agendaViewMode === 'directory'
                    ? 'bg-[#0A3D27] text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Speaker Directory ({allDirectorySpeakers.length})
              </button>
            </div>
          </div>

          {/* SIDE-BY-SIDE CHRONOLOGICAL AGENDA: BOTH DAYS DISPLAYED AT HALF WIDTH */}
          {agendaViewMode === 'agenda' ? (
            <div className="space-y-4">
              {/* Track Filter & Personal Schedule Bar */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white border border-slate-200 rounded-2xl p-3 shadow-2xs">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[11px] font-bold text-slate-500 px-1.5">Track:</span>
                  <button
                    type="button"
                    onClick={() => setSelectedAgendaTrack('all')}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
                      selectedAgendaTrack === 'all'
                        ? 'bg-[#0A3D27] text-white shadow-2xs'
                        : 'bg-slate-100 text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    All Tracks
                  </button>
                  {agendaTracks.map((track) => (
                    <button
                      key={track}
                      type="button"
                      onClick={() => setSelectedAgendaTrack(track)}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
                        selectedAgendaTrack === track
                          ? 'bg-[#0A3D27] text-white shadow-2xs'
                          : 'bg-slate-100 text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      {track}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {bookmarkedIds.length > 0 && (
                    <Link
                      to="/my-iiab?tab=meetings"
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-[#FAF5EA] border border-[#E6C687] text-[#785613] text-[11px] font-extrabold hover:bg-[#F3E9D2] transition-colors"
                    >
                      <BookmarkCheck className="w-3.5 h-3.5" />
                      <span>{bookmarkedIds.length} Saved in My Schedule →</span>
                    </Link>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                {/* DAY 1 COLUMN */}
                <div className="space-y-3">
                  <div className="bg-[#0A3D27] text-white rounded-2xl p-3.5 shadow-xs flex items-center justify-between border border-emerald-800">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-black uppercase tracking-wider text-[#E6C687] bg-white/10 px-2 py-0.5 rounded-full">
                          Day 1
                        </span>
                        <span className="text-xs font-bold text-emerald-200">Tuesday, 27 July 2027</span>
                      </div>
                      <div className="text-xs font-extrabold text-white">
                        Policy, Biologicals & AI Precision
                      </div>
                    </div>
                    <span className="text-[10px] font-extrabold text-emerald-200 bg-white/10 px-2.5 py-1 rounded-full shrink-0">
                      {day1Sessions.length} Sessions
                    </span>
                  </div>

                  <div className="space-y-3">
                    {day1Sessions.map((session) => (
                      <CompactSessionCard
                        key={session.id}
                        session={session}
                        isBookmarked={bookmarkedIds.includes(session.id)}
                        onToggleBookmark={handleToggleSessionBookmark}
                      />
                    ))}
                  </div>
                </div>

                {/* DAY 2 COLUMN */}
                <div className="space-y-3">
                  <div className="bg-[#0A3D27] text-white rounded-2xl p-3.5 shadow-xs flex items-center justify-between border border-emerald-800">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-black uppercase tracking-wider text-[#E6C687] bg-white/10 px-2 py-0.5 rounded-full">
                          Day 2
                        </span>
                        <span className="text-xs font-bold text-emerald-200">Wednesday, 28 July 2027</span>
                      </div>
                      <div className="text-xs font-extrabold text-white">
                        Formulations, Cold Chains & Robotics
                      </div>
                    </div>
                    <span className="text-[10px] font-extrabold text-emerald-200 bg-white/10 px-2.5 py-1 rounded-full shrink-0">
                      {day2Sessions.length} Sessions
                    </span>
                  </div>

                  <div className="space-y-3">
                    {day2Sessions.map((session) => (
                      <CompactSessionCard
                        key={session.id}
                        session={session}
                        isBookmarked={bookmarkedIds.includes(session.id)}
                        onToggleBookmark={handleToggleSessionBookmark}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* SPEAKER DIRECTORY VIEW (COMPACT UNIFIED GRID) */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {allDirectorySpeakers.map((sp) => (
                <div
                  key={sp.id}
                  className="bg-white border border-slate-200/90 rounded-2xl p-4 flex flex-col justify-between group hover:border-[#0A3D27]/30 hover:shadow-xs transition-all"
                >
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl overflow-hidden bg-slate-100 border border-slate-200 shrink-0">
                        <SpeakerAvatar src={sp.imageSrc} alt={sp.name} name={sp.name} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className={`text-[9px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full inline-block ${
                          sp.isKeynote
                            ? 'text-[#0A3D27] bg-[#0A3D27]/10 border border-[#0A3D27]/20'
                            : 'text-[#785613] bg-[#FAF5EA] border border-[#E6C687]'
                        }`}>
                          {sp.isKeynote ? 'Keynote Speaker' : 'Industry Panelist'}
                        </span>
                        <h5 className="text-xs font-bold text-slate-900 group-hover:text-[#0A3D27] transition-colors truncate mt-1">
                          {sp.name}
                        </h5>
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-600 line-clamp-2 font-medium">
                      {sp.role}
                    </p>
                    <p className="text-[10px] text-[#0A3D27] font-semibold truncate">
                      {sp.organization}
                    </p>
                  </div>

                  <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-500">
                    <span className="truncate">{sp.stageOrHall}</span>
                    {sp.time && <span className="font-mono text-[9px] shrink-0 ml-1">{sp.time}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Dedicated Call for Speakers CTA Banner */}
        <div className="relative overflow-hidden bg-gradient-to-br from-[#FAF5EA] via-[#FDFBF7] to-[#FAF5EA] border border-[#E6C687] rounded-3xl p-8 sm:p-10 shadow-xs">
          {/* Subtle decorative ambient glow */}
          <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#0A3D27]/5 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-[#E6C687]/20 rounded-full blur-xl pointer-events-none" />

          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="space-y-3 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0A3D27]/10 border border-[#0A3D27]/20 text-[#0A3D27] text-xs font-bold">
                <Sparkles className="w-3.5 h-3.5 text-[#0A3D27]" />
                <span>Call for Speakers & Keynote Papers</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                Share Your Insights on the National Agri-Business Stage
              </h3>
              <p className="text-xs sm:text-sm text-slate-700 max-w-2xl leading-relaxed">
                Are you an agritech innovator, researcher, or agricultural policy leader? Submit your proposal to speak in front of 3,000+ enterprise leaders and policymakers in the 128 PAX Conference Hall across 6 specialized tracks.
              </p>

              {/* Key Speaker Perks */}
              <div className="pt-2 flex flex-wrap items-center justify-center lg:justify-start gap-2.5 text-[11px] font-semibold text-slate-700">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/80 border border-[#E6C687]/60 shadow-2xs">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  6 Specialized Industry Tracks
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/80 border border-[#E6C687]/60 shadow-2xs">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  128 PAX Dedicated Stage
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/80 border border-[#E6C687]/60 shadow-2xs">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  Complimentary VIP Speaker Access
                </span>
              </div>
            </div>

            <div className="shrink-0 flex flex-col items-center sm:items-end gap-2.5 w-full sm:w-auto">
              <Link
                to="/speakers/apply"
                className="w-full sm:w-auto px-7 py-4 bg-[#0A3D27] hover:bg-[#062B1B] text-white font-extrabold text-sm rounded-xl shadow-lg shadow-[#0A3D27]/25 hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2.5 group cursor-pointer"
              >
                <Mic className="w-4 h-4 text-[#E6C687]" />
                <span>Apply as Speaker</span>
                <ArrowRight className="w-4 h-4 text-[#E6C687] group-hover:translate-x-1 transition-transform" />
              </Link>
              <p className="text-[11px] text-slate-500 font-medium text-center sm:text-right">
                Rolling submissions • Priority review for early abstracts
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. SPLIT-SCREEN SPONSORSHIP COMMAND CENTER & PAST PATRONS SHOWCASE */}
      <section id="sponsors" className="space-y-8 scroll-mt-24">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-2 border-b border-slate-200">
          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0A3D27]/10 border border-[#0A3D27]/20 text-[#0A3D27] text-xs font-bold">
              <Sparkles className="w-3.5 h-3.5 text-[#0A3D27]" />
              <span>Sponsorships & Strategic Alliances</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
              Sponsors & Industry Patrons
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 max-w-2xl leading-relaxed">
              Official sponsorship packages for IIAB 2027 are now open. Showcase your brand on India's premier agribusiness stage alongside our distinguished past edition supporters.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-900 text-xs font-bold shadow-2xs">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              2027 Opportunities Open
            </span>
          </div>
        </div>

        {/* Split-Screen Command Center */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-stretch">
          {/* Left Column (5 cols): 2027 Sponsorship Command Card */}
          <div className="lg:col-span-5 bg-gradient-to-br from-[#FAF5EA] via-[#FDFBF7] to-[#F5EFEB] rounded-3xl p-6 sm:p-8 border border-[#E6C687] shadow-md relative overflow-hidden flex flex-col justify-between">
            {/* Ambient warm glow */}
            <div className="absolute -top-16 -right-16 w-56 h-56 bg-[#E6C687]/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-[#0A3D27]/5 rounded-full blur-2xl pointer-events-none" />

            <div className="relative z-10 space-y-6">
              {/* Badge */}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#E6C687] text-[#785613] text-xs font-bold shadow-2xs">
                  <Award className="w-3.5 h-3.5 text-[#C5A059]" />
                  <span>Sponsor IIAB 2027</span>
                </div>
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#785613] bg-[#FAF5EA] px-2.5 py-0.5 rounded-full border border-[#E6C687]">
                  Pavilion 3 • JWCC Mumbai
                </span>
              </div>

              {/* Pitch */}
              <div>
                <h3 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight leading-tight">
                  Position Your Enterprise at the Forefront of Agri-Innovation
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 mt-2.5 leading-relaxed">
                  Join 3,000+ C-suite leaders, agchem innovators, policy architects, and international researchers at Jio World Convention Centre, Mumbai (27–28 July 2027).
                </p>
              </div>

              {/* Key Entitlements / Highlights */}
              <div className="space-y-3 pt-1">
                <div className="flex items-start gap-2.5 text-xs">
                  <div className="w-5 h-5 rounded-md bg-white border border-[#E6C687] text-[#785613] flex items-center justify-center shrink-0 mt-0.5 shadow-2xs">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#0A3D27]" />
                  </div>
                  <div>
                    <span className="font-bold text-slate-900">Plenary Stage Keynotes:</span>{' '}
                    <span className="text-slate-600">Keynote presentations to a 128 PAX C-level audience.</span>
                  </div>
                </div>

                <div className="flex items-start gap-2.5 text-xs">
                  <div className="w-5 h-5 rounded-md bg-white border border-[#E6C687] text-[#785613] flex items-center justify-center shrink-0 mt-0.5 shadow-2xs">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#0A3D27]" />
                  </div>
                  <div>
                    <span className="font-bold text-slate-900">Curated B2B Matchmaking:</span>{' '}
                    <span className="text-slate-600">Private lounge for pre-scheduled 30-min buyer meetings.</span>
                  </div>
                </div>

                <div className="flex items-start gap-2.5 text-xs">
                  <div className="w-5 h-5 rounded-md bg-white border border-[#E6C687] text-[#785613] flex items-center justify-center shrink-0 mt-0.5 shadow-2xs">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#0A3D27]" />
                  </div>
                  <div>
                    <span className="font-bold text-slate-900">Category Exclusivity:</span>{' '}
                    <span className="text-slate-600">Sole title branding across Agrochem, Bio-inputs, or AgriTech.</span>
                  </div>
                </div>

                <div className="flex items-start gap-2.5 text-xs">
                  <div className="w-5 h-5 rounded-md bg-white border border-[#E6C687] text-[#785613] flex items-center justify-center shrink-0 mt-0.5 shadow-2xs">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#0A3D27]" />
                  </div>
                  <div>
                    <span className="font-bold text-slate-900">100% Tax Deductible:</span>{' '}
                    <span className="text-slate-600">GST SAC Code 998596 with full 18% Input Tax Credit.</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="relative z-10 pt-6 mt-6 border-t border-[#E6C687]/60 space-y-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={eventInfo.brochureUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-4 py-3.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white font-extrabold text-xs rounded-xl shadow-md shadow-[#0A3D27]/20 transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Download className="w-4 h-4 text-[#E6C687]" />
                  <span>Download 2027 Prospectus</span>
                </a>
                <a
                  href="mailto:contact@rayconsulting.in?subject=IIAB%202027%20Sponsorship%20Inquiry"
                  className="px-4 py-3.5 bg-white hover:bg-slate-50 text-[#0A3D27] border border-[#0A3D27]/30 font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-2xs"
                >
                  <Mail className="w-4 h-4 text-[#C5A059]" />
                  <span>Enquire for Tiers</span>
                </a>
              </div>
              <p className="text-[11px] text-slate-500 text-center sm:text-left">
                Early-bird allocations for Title, Gold, and Experiential entitlements are now active.
              </p>
            </div>
          </div>

          {/* Right Column (7 cols): High-Contrast Logo-Forward Past Sponsor Wall */}
          <div className="lg:col-span-7 flex flex-col justify-between space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3 pb-2 border-b border-slate-200">
                <div>
                  <span className="text-[11px] font-extrabold uppercase tracking-wider text-[#0A3D27] block">
                    Previous Edition Patrons
                  </span>
                  <h3 className="text-base sm:text-lg font-black text-slate-900 tracking-tight">
                    Distinguished Brands That Powered Past Summits
                  </h3>
                </div>
                <span className="text-[10px] font-semibold text-slate-600 bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200 shrink-0">
                  Past Sponsors
                </span>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Honoring the corporate pioneers and diplomatic partners from previous editions of IIAB. Official 2027 partnership allocations are now in progress.
              </p>
            </div>

            {/* 4x4 High-Density Logo Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {pastEditionSponsors.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white border border-slate-200/90 hover:border-[#0A3D27] rounded-2xl p-3 flex flex-col items-center justify-between hover:shadow-md transition-all duration-200 group text-center"
                >
                  {/* Generous Logo Frame (Logo is the Hero) */}
                  <div className="h-16 w-full flex items-center justify-center p-1.5">
                    <img
                      src={item.logoSrc}
                      alt={item.name}
                      className="max-h-12 max-w-[90%] object-contain group-hover:scale-105 transition-transform duration-200"
                      loading="lazy"
                    />
                  </div>

                  {/* Clean, discreet caption */}
                  <div className="pt-2 w-full border-t border-slate-100">
                    <span
                      className="text-[11px] font-bold text-slate-800 block truncate group-hover:text-[#0A3D27] transition-colors"
                      title={item.name}
                    >
                      {item.name}
                    </span>
                    <span className="text-[9px] font-medium text-slate-400 block truncate">
                      {item.pastTier}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Note on 2027 availability */}
            <div className="bg-[#FAF5EA] border border-[#E6C687]/60 rounded-xl px-4 py-2.5 flex items-center justify-between gap-3 text-[11px] text-[#785613]">
              <span className="font-semibold">
                ⭐ Looking to secure one of these signature categories for IIAB 2027?
              </span>
              <a
                href="mailto:contact@rayconsulting.in?subject=IIAB%202027%20Category%20Exclusivity%20Inquiry"
                className="font-bold underline shrink-0 hover:text-amber-900 cursor-pointer"
              >
                Reserve Category →
              </a>
            </div>
          </div>
        </div>

        {/* Streamlined Institutional Alliances & Media Strip */}
        <div className="pt-4 border-t border-slate-200/80 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#0A3D27]" />
              <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                Institutional Alliances & Media Partners
              </span>
              <span className="text-[11px] text-slate-400 font-medium">(Previous Editions)</span>
            </div>
            <span className="text-[11px] text-slate-500 font-medium hidden sm:inline">
              National trade federations, international councils & official agri-press
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2.5">
            {industryPartners.map((partner, i) => (
              <div
                key={i}
                className="bg-white border border-slate-200/80 rounded-xl p-2 flex flex-col items-center justify-center gap-1.5 shadow-2xs hover:border-[#0A3D27]/30 hover:shadow-xs transition-all text-center group"
              >
                <div className="h-8 w-full flex items-center justify-center p-0.5">
                  <img
                    src={partner.logoSrc}
                    alt={partner.name}
                    className="max-h-6 max-w-full object-contain group-hover:scale-105 transition-transform"
                    loading="lazy"
                  />
                </div>
                <span className="text-[10px] font-bold text-slate-700 truncate w-full" title={partner.name}>
                  {partner.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. CONSOLIDATED VENUE & TRAVEL LOGISTICS */}
      <VenueMapSection />

      {/* 7. INTERACTIVE EXHIBITION FLOOR PLAN & RESERVATION ENGINE */}
      <section id="floorplan-showcase" className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#041D12] via-[#062B1B] to-[#0A3D27] border border-[#E6C687]/30 shadow-2xl text-white">
        {/* Architectural Blueprint Background Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none bg-[radial-gradient(#E6C687_1px,transparent_1px)] [background-size:24px_24px]" />
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-[#C5A059]/15 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 p-6 sm:p-10 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* Left Column: Commercial Telemetry & Reservation Details */}
            <div className="lg:col-span-7 space-y-6">
              {/* Title & Narrative (Exhibitor Call to Action) */}
              <div className="space-y-3">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight leading-tight">
                  Book Your Prime Exhibition Stall — <br />
                  <span className="bg-gradient-to-r from-[#FAF5EA] via-[#E6C687] to-[#C5A059] bg-clip-text text-transparent">
                    Position Your Brand Before 5,000+ Agri Decision Makers
                  </span>
                </h2>
                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-normal max-w-xl">
                  Browse 120 prime exhibition stalls across 6 tailored booth formats. Select your preferred stall location on the live floor plan, secure guaranteed reservation holds during checkout, and receive instant GST-compliant tax invoices.
                </p>
              </div>

              {/* Live Telemetry Grid (3 Tactile Metric Cards) */}
              <div className="grid grid-cols-3 gap-3 sm:gap-4 pt-1">
                {/* Available */}
                <div className="bg-white/5 border border-emerald-500/30 rounded-2xl p-3 sm:p-4 backdrop-blur-sm transition-all hover:bg-white/8 hover:border-emerald-400/50 group">
                  <div className="flex items-center justify-between text-xs text-emerald-300 mb-1 font-semibold">
                    <span className="flex items-center gap-1.5 text-[11px] sm:text-xs">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      Available
                    </span>
                  </div>
                  <div className="text-xl sm:text-3xl font-black text-white group-hover:text-emerald-200 transition-colors">
                    {availableCount}
                  </div>
                  <div className="text-[10px] sm:text-[11px] text-emerald-400/80 mt-1 font-medium">Ready to reserve</div>
                </div>

                {/* On Hold */}
                <div className="bg-white/5 border border-amber-500/30 rounded-2xl p-3 sm:p-4 backdrop-blur-sm transition-all hover:bg-white/8 hover:border-amber-400/50 group">
                  <div className="flex items-center justify-between text-xs text-amber-300 mb-1 font-semibold">
                    <span className="flex items-center gap-1.5 text-[11px] sm:text-xs">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                      On Hold
                    </span>
                  </div>
                  <div className="text-xl sm:text-3xl font-black text-white group-hover:text-amber-200 transition-colors">
                    {onHoldCount}
                  </div>
                  <div className="text-[10px] sm:text-[11px] text-amber-400/80 mt-1 font-medium">In active carts</div>
                </div>

                {/* Booked */}
                <div className="bg-white/5 border border-slate-600/40 rounded-2xl p-3 sm:p-4 backdrop-blur-sm transition-all hover:bg-white/8 hover:border-slate-400/50 group">
                  <div className="flex items-center justify-between text-xs text-slate-300 mb-1 font-semibold">
                    <span className="flex items-center gap-1.5 text-[11px] sm:text-xs">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                      Booked
                    </span>
                  </div>
                  <div className="text-xl sm:text-3xl font-black text-white group-hover:text-slate-200 transition-colors">
                    {bookedCount}
                  </div>
                  <div className="text-[10px] sm:text-[11px] text-slate-400 mt-1 font-medium">Secured brands</div>
                </div>
              </div>

              {/* Commercial Stall Categories Showcase */}
              <div className="space-y-2 pt-1">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  Available Stall Configurations:
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {[
                    { name: '3×3m Shell Scheme', tag: 'Turnkey standard' },
                    { name: '4×3m Premium', tag: 'Aisle frontage' },
                    { name: '6×3m Grand', tag: 'Dual frontage' },
                    { name: '6×6m Island', tag: '360° open' },
                    { name: 'Trading Tables', tag: 'Commodity desk' },
                    { name: 'Meeting Rooms', tag: 'Private suite' },
                  ].map((format) => (
                    <div
                      key={format.name}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-white/5 border border-white/10 text-slate-200 text-xs"
                    >
                      <span className="font-bold text-white text-[11px]">{format.name}</span>
                      <span className="text-[10px] text-[#E6C687]">({format.tag})</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Link
                  to="/floorplan"
                  className="px-6 py-3.5 bg-gradient-to-r from-[#E6C687] via-[#D4AF37] to-[#C5A059] hover:from-[#DFB865] hover:to-[#B38F30] text-slate-950 font-black text-sm rounded-xl transition-all shadow-lg shadow-black/30 hover:scale-[1.02] active:scale-[0.98] flex items-center gap-2 cursor-pointer group"
                >
                  <Layers className="w-4 h-4 text-slate-950 transition-transform group-hover:rotate-6" />
                  <span>Explore Floor Plan</span>
                  <ArrowRight className="w-4 h-4 text-slate-950 transition-transform group-hover:translate-x-1" />
                </Link>

                <Link
                  to="/register"
                  className="px-5 py-3.5 bg-white/10 hover:bg-white/15 border border-white/20 text-white font-bold text-sm rounded-xl transition-all hover:border-white/40 flex items-center gap-2 cursor-pointer"
                >
                  <Ticket className="w-4 h-4 text-[#E6C687]" />
                  <span>Register to Attend</span>
                </Link>
              </div>
            </div>

            {/* Right Column: Architectural Schematic 2D Mini-Map Preview Card */}
            <div className="lg:col-span-5">
              <Link
                to="/floorplan"
                className="block relative bg-[#07130D] border border-emerald-500/30 hover:border-[#E6C687]/60 rounded-2xl p-5 shadow-2xl transition-all group overflow-hidden"
              >
                {/* CAD Blueprint Top Inspector Bar */}
                <div className="flex items-center justify-between pb-3 mb-3 border-b border-emerald-900/60 text-[11px] font-mono">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" />
                    <span className="text-emerald-400 font-bold ml-2">JWCC • PAVILION 3</span>
                  </div>
                  <div className="flex items-center gap-1 text-slate-400 text-[10px]">
                    <span>SCALE 1:100</span>
                    <Maximize2 className="w-3 h-3 text-[#E6C687] ml-1 group-hover:scale-125 transition-transform" />
                  </div>
                </div>

                {/* Simulated 2D Architectural Schematic Blueprint */}
                <div className="relative bg-[#040C07] border border-emerald-900/50 rounded-xl p-4 space-y-3 font-mono">
                  {/* North Entry: Turnstiles & Registration */}
                  <div className="flex items-center justify-between px-3 py-1.5 bg-emerald-950/80 border border-emerald-600/30 rounded-lg text-[10px] text-emerald-300">
                    <span className="flex items-center gap-1.5 font-bold">
                      <ScanLine className="w-3 h-3 text-emerald-400" />
                      MAIN ENTRY & REGISTRATION
                    </span>
                    <span className="text-emerald-400/70 text-[9px]">TURNSTILES</span>
                  </div>

                  {/* Hall Layout Grid */}
                  <div className="grid grid-cols-12 gap-2 text-[9px] py-1">
                    {/* Left: Keynote Hall (128 PAX) */}
                    <div className="col-span-4 bg-emerald-950/40 border border-emerald-700/30 rounded-lg p-2 flex flex-col justify-between">
                      <div className="text-emerald-200 font-bold leading-tight">CONFERENCE ARENA</div>
                      <div className="text-[8px] text-slate-400 mt-1">128 PAX Plenary Stage & Keynotes</div>
                      <div className="mt-2 text-[8px] font-semibold text-[#E6C687] bg-[#E6C687]/10 px-1.5 py-0.5 rounded text-center">
                        KEYNOTES
                      </div>
                    </div>

                    {/* Center: Main Stalls Showcase */}
                    <div className="col-span-8 space-y-2">
                      {/* Island Stalls Row */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-emerald-900/40 border border-emerald-400/50 rounded-lg p-2 text-center">
                          <div className="font-bold text-white text-[10px]">A-01 [6×6m]</div>
                          <div className="text-[8px] text-emerald-300 font-semibold">ISLAND • AVAILABLE</div>
                        </div>
                        <div className="bg-amber-950/40 border border-amber-400/50 rounded-lg p-2 text-center">
                          <div className="font-bold text-white text-[10px]">A-02 [6×6m]</div>
                          <div className="text-[8px] text-amber-300 font-semibold">ISLAND • ON HOLD</div>
                        </div>
                      </div>

                      {/* Premium & Shell Scheme Rows */}
                      <div className="grid grid-cols-4 gap-1.5">
                        <div className="bg-emerald-900/20 border border-emerald-500/30 rounded p-1.5 text-center">
                          <div className="font-bold text-slate-200 text-[8px]">B-01</div>
                          <div className="text-[7px] text-emerald-400">4×3m</div>
                        </div>
                        <div className="bg-emerald-900/20 border border-emerald-500/30 rounded p-1.5 text-center">
                          <div className="font-bold text-slate-200 text-[8px]">B-02</div>
                          <div className="text-[7px] text-emerald-400">4×3m</div>
                        </div>
                        <div className="bg-slate-800/40 border border-slate-600/40 rounded p-1.5 text-center">
                          <div className="font-bold text-slate-400 text-[8px]">C-01</div>
                          <div className="text-[7px] text-slate-400">BOOKED</div>
                        </div>
                        <div className="bg-emerald-900/20 border border-emerald-500/30 rounded p-1.5 text-center">
                          <div className="font-bold text-slate-200 text-[8px]">C-02</div>
                          <div className="text-[7px] text-emerald-400">3×3m</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* South: B2B Meeting Lounge & VIP Suites */}
                  <div className="flex items-center justify-between px-3 py-1.5 bg-slate-900/70 border border-slate-700/40 rounded-lg text-[9px] text-slate-300">
                    <span className="font-semibold text-slate-300">B2B DELEGATE LOUNGE & COMMODITY TRADING TABLES</span>
                    <span className="text-[#E6C687] text-[8px] font-bold">PAVILION 3 SOUTH</span>
                  </div>
                </div>

                {/* Card Bottom CTA overlay */}
                <div className="mt-3 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 text-[10px] text-slate-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    <span>Live SVG Coordinate Map</span>
                  </div>
                  <div className="text-[#E6C687] font-bold text-xs flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                    <span>Click to Enter Canvas</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;


